import { Loader } from "@googlemaps/js-api-loader";


export const loader = new Loader( {
    apiKey: Nu.maps.api_key,
    version: "beta",
} );

/**
 * @typedef MapItem
 * @property {string} id
 * @property {string} title
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} image
 * @property {string} permalink
 * @property {string} excerpt
 */

/**
 * @property {ShadowRoot} shadowRoot
 */
export class NuMap extends HTMLElement {
    static observedAttributes = ['zoom', 'center-lat', 'center-lng', 'map-id', 'no-panel'];

    /**
     * @type {Array<MapItem>}
     */
    items = [];
    /**
     * @type {HTMLImageElement}
     */
    //@ts-ignore
    pinImg = null;
    pinImageURL = '/wp-content/themes/nu-inclu/assets/images/map-pin.svg';

    constructor() {
        super();

        this.pinImg = document.createElement( 'img' );
        this.pinImg.src = this.pinImageURL;
        this.pinImg.alt = 'Map pin';
        this.pinImg.style.width = '2rem';
        this.pinImg.style.height = '2rem';
    }

    connectedCallback() {
        this.getPanelTemplate();
        this.initMap();
    }

    async initMap() {
        const [
            { AdvancedMarkerElement },
            { Map, OverlayView }
        ] = await Promise.all( [
            loader.importLibrary( 'marker' ),
            loader.importLibrary( "maps" )
        ] );

        this.map = new Map( this, {
            zoom: Number( this.getAttribute( 'zoom' ) ),
            center: this.getCenter(),
            mapId: this.getAttribute( 'map-id' ),
            fullscreenControl: false,
            scrollWheelControl: false,
            streetViewControl: false,
        } );

        const markers = this.getItems().map( item => {
            if ( !item.latitude || !item.longitude ) return;

            const marker = new AdvancedMarkerElement( {
                title: item.title,
                map: this.map,
                position: {
                    lat: Number( item.latitude ),
                    lng: Number( item.longitude )
                },
                content: this.pinImg.cloneNode(),
                gmpClickable: true,
            } );

            marker.setAttribute( 'data-item-id', item?.id );

            return marker;
        } ).filter( ( item ) => item );

        if ( this.hasAttribute( 'no-panel' ) ) return;

        if ( !this.template ) return;

        class NuOverlayView extends OverlayView {
            /**
             * @type {string}
             */
            id;

            /**
             * @type {HTMLElement}
             */
            panel;

            static breakpoint = window.matchMedia( '(min-width: 640px)' );

            /**
             * @param {NuMap} parent
             */
            constructor( parent ) {
                super();
                this.id = null;
                this.panel = null;  // Reference to the div containing the overlay
                this.parent = parent;
            }

            onAdd() {
                const isNew = this.panel?.id !== this.id?.toString();

                if ( isNew ) {
                    this.panel?.remove();
                    this.panel = this.parent.getItemPanel( this.id );
                    this.parent.appendChild( this.panel );
                }
                this.panel.style.position = 'absolute';
                this.panel.style.transformOrigin = '0 0';

                if ( NuOverlayView.breakpoint.matches ) {
                    this.panel.style.setProperty( 'inset', '0 -100rem auto 0' );
                } else {
                    this.panel.style.setProperty( 'inset', '5%' );
                }

                if ( isNew ) {
                    this.panel.animate( {
                        transform: ['scale(0.8)', 'scale(1)'],
                    }, {
                        duration: 400,
                        easing: 'cubic-bezier(.4,1.21,.72,1.05)',
                    } );
                    this.panel.animate( {
                        opacity: ['0', '1'],
                    }, {
                        duration: 400,
                        easing: 'ease',
                    } );
                }
            }

            // Called when the map is panned or zoomed, to update the position of the overlay
            draw() {
                if ( !this.panel ) return;

                if ( NuOverlayView.breakpoint.matches ) {
                    this.panel.style.setProperty( 'inset', '0 -100rem auto 0' );

                    const projection = this.getProjection();
                    const position = projection.fromLatLngToContainerPixel( this.position );

                    if ( position ) {
                        this.panel.style.setProperty( 'translate', `${position.x}px ${position.y}px` );
                    }
                } else {
                    this.panel.style.setProperty( 'inset', '5%' );
                    this.panel.style.setProperty( 'translate', `0px 0px` );
                }
            }

            // Set position of the overlay
            setPosition( latLng ) {
                this.position = latLng;
                this.draw();
            }

            // Called when the overlay is removed
            onRemove() {
                if ( this.panel ) {
                    this.panel.remove();
                }
                this.panel = null;
            }
        }

        const overlay = new NuOverlayView( this );

        markers.forEach( marker => {

            marker.addEventListener( 'gmp-click', ( e ) => {
                /** REQUIRED to prevent maps scrolling the window all over the place */
                let scroll = window.scrollY;
                requestAnimationFrame( () => {
                    window.scrollTo( { top: scroll } );
                } );
                overlay.id = e.srcElement.getAttribute( 'data-item-id' );
                overlay.setMap( this.map );
                overlay.setPosition( marker.position );
            } );
        } );
    }

    getCenter() {
        let lat = 0;
        let lng = 0;
        let num = 0;

        // Array reduce to get the mean average of all latitudes and longitudes
        this.getItems().forEach( item => {
            if ( !item.latitude || !item.longitude ) return;

            lat = lat + Number( item.latitude );
            lng = lng + Number( item.longitude );
            num++;
        } );

        if ( num === 0 ) return { lat, lng };

        lat = lat / num;
        lng = lng / num;

        return { lat, lng };
    }

    /**
     * @return {Array<MapItem>}
     */
    getItems() {
        if ( this.items.length > 0 ) return this.items;

        this.items = JSON.parse( this.getAttribute( 'items' ) ?? '[]' ) || [];

        return this.items;
    }

    /**
     * @return {HTMLTemplateElement}
     */
    getPanelTemplate() {
        if ( !this.template ) {
            this.template = /** */ ( this.querySelector( 'template' ) );
        }
        if ( !this.template && !this.hasAttribute( 'no-panel' ) ) throw new Error( 'No <template> found in NuMap component' );
        return this.template;
    }

    /**
     * @param {string} id The item id to render the panel for
     * @return {HTMLElement|null}
     */
    getItemPanel( id ) {
        const item = /** @type {MapItem} */ ( this.getItems().find( ( item ) => item.id == id ) );
        if ( !item ) return null;

        const panel = this.getPanelTemplate().content?.cloneNode( true );

        const panelContent = panel.firstElementChild;
        panelContent.setAttribute( 'id', id.toString() );
        const titleSlot = panelContent.querySelector( '[data-title]' );
        const linkSlot = panelContent.querySelector( '[data-permalink]' );
        const excerptSlot = panelContent.querySelector( '[data-excerpt]' );
        const imageSlot = panelContent.querySelector( '[data-thumbnail]' );
        const parentSlot = panelContent.querySelector( '[data-parent]' );
        const closer = panelContent.querySelector( '[data-close]' );

        titleSlot.textContent = item?.title || '';
        linkSlot.href = item?.permalink || '';
        imageSlot.innerHTML = item?.image || '';
        excerptSlot.innerHTML = item?.excerpt || '';

        if ( parentSlot && item.parent ) {
            parentSlot.textContent = item?.parent_name || '';
            parentSlot.setAttribute( 'href', item?.parent_link || '' );
        }

        closer.addEventListener( 'click', ( _e ) => {
            panelContent.remove();
        } );

        return panelContent;
    }
}

customElements.define( 'nu-map', NuMap );

